import React, { useState } from 'react';
import { Loader2, CheckCircle } from 'lucide-react';
import { SEO } from '../../../shared/components';
import { useAuthContext } from '../context/AuthContext';
import { AuthShell, authInputStyle, authInputFocus, authInputBlur, authLabelStyle, authButtonStyle } from '../components/AuthShell';

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const { forgotPassword } = useAuthContext();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) { setError('Please enter your email'); return; }
    setError(null);
    setIsSubmitting(true);
    try {
      const result = await forgotPassword({ email });
      if (result.success) { setIsSuccess(true); } else { setError(result.error || 'Failed to send reset email'); }
    } catch (err: any) {
      setError(err.message || 'Network error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <>
        <SEO title="Check Your Email - RRD Planners" description="Password reset email sent" />
        <AuthShell>
          <div style={{ textAlign: 'center' }}>
            <CheckCircle size={48} style={{ color: '#28a745', margin: '0 auto 16px' }} />
            <h2 style={{ color: '#fff', fontSize: 24, fontWeight: 700, marginBottom: 8 }}>Check Your Email</h2>
            <p style={{ color: '#888', fontSize: 14, marginBottom: 24, lineHeight: 1.6 }}>
              If an account exists with <span style={{ color: '#FFD93D' }}>{email}</span>, we've sent a password reset link.
            </p>
            <a href="/auth/login" style={{
              display: 'inline-block', padding: '12px 32px', background: '#FFD93D', color: '#1a1a1a',
              borderRadius: 10, fontWeight: 700, textDecoration: 'none', fontSize: 15
            }}>
              Back to Sign In
            </a>
          </div>
        </AuthShell>
      </>
    );
  }

  return (
    <>
      <SEO title="Forgot Password - RRD Planners" description="Reset your password" />
      <AuthShell>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <h2 style={{ color: '#fff', fontSize: 28, fontWeight: 700 }}>Forgot Password</h2>
          <p style={{ color: '#666', fontSize: 14, marginTop: 8 }}>Enter your email to receive a reset link</p>
        </div>

        <form onSubmit={handleSubmit}>
          {error && (
            <div style={{ background: 'rgba(229,115,115,0.1)', border: '1px solid #e57373', borderRadius: 10, padding: '10px 14px', marginBottom: 16, color: '#e57373', fontSize: 13 }}>
              {error}
            </div>
          )}

          <div style={{ marginBottom: 20 }}>
            <label style={authLabelStyle}>Email</label>
            <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
              placeholder="Enter your email" autoComplete="email" style={authInputStyle} onFocus={authInputFocus} onBlur={authInputBlur} />
          </div>

          <button type="submit" disabled={isSubmitting} style={authButtonStyle(isSubmitting)}>
            {isSubmitting ? <><Loader2 size={18} className="animate-spin" /> Sending...</> : 'Send Reset Link'}
          </button>
        </form>

        <p style={{ color: '#666', fontSize: 14, marginTop: 24, textAlign: 'center' }}>
          Remember your password?{' '}
          <a href="/auth/login" style={{ color: '#FFD93D', fontWeight: 600, textDecoration: 'none' }}>Sign in</a>
        </p>
      </AuthShell>
    </>
  );
};

export default ForgotPasswordPage;
